import { Injectable, NgZone } from '@angular/core';
import type { IOpusRecorder } from 'opus-recorder';
import { defer, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';

export interface VoiceResult { blob: Blob; duration: number }

export enum VoiceFormat {
  OGG = 'ogg',
  WAV = 'wav',
}

export interface VoiceStartOptions {
  format?: VoiceFormat;
}

const encoderPath = '/assets/opus-recorder/encoderWorker.min.js';
const BASE_OPTIONS = { encoderPath, reuseWorker: true };
const MIN_RECORDING_TIME_MS = 800;

const MIME_BY_FORMAT: Record<VoiceFormat, BlobPropertyBag> = {
  [VoiceFormat.OGG]: { type: 'audio/ogg' },
  [VoiceFormat.WAV]: { type: 'audio/wav' },
};

@Injectable({ providedIn: 'root' })
export class VoiceRecordingService {
  private OpusRecorder?: IOpusRecorder;

  private rec?: IOpusRecorder;
  private chunks: Uint8Array[] = [];
  private startedAt = 0;
  private currentFormat: VoiceFormat = VoiceFormat.OGG;

  constructor(private zone: NgZone) {
  }

  private ensureModule$(): Observable<void> {
    if (this.OpusRecorder) return of(void 0);

    return from(import('opus-recorder' as any)).pipe(
      tap(mod => (this.OpusRecorder = mod.default)),
      map(() => void 0),
      catchError(() => throwError(() => 'failed_to_start_recording')),
    );
  }

  start$(opts: VoiceStartOptions = {}): Observable<{ stop$: () => Observable<VoiceResult> }> {
    return this.ensureModule$().pipe(
      mergeMap(() =>
        defer(() => {
          this.currentFormat = opts?.format ?? VoiceFormat.OGG;

          this.rec = new (this.OpusRecorder as any)({
            ...BASE_OPTIONS,
            encoderFormat: this.currentFormat,
            encoderSampleRate: 48000,
          });

          this.chunks = [];
          this.startedAt = Date.now();
          this.rec!.ondataavailable = (u8: Uint8Array) => this.chunks.push(u8);

          const startPromise = this.zone.runOutsideAngular(async () => this.rec!.start());

          return from(startPromise).pipe(
            map(() => ({
              stop$: () =>
                new Observable<VoiceResult>(observer => {
                  const delay = Math.max(0, this.startedAt + MIN_RECORDING_TIME_MS - Date.now());

                  this.rec!.onstop = () => {
                    try {
                      const blob = new Blob(this.chunks as BlobPart[], MIME_BY_FORMAT[this.currentFormat]);
                      const duration = Math.max(1, Math.round((Date.now() - this.startedAt) / 1000));
                      this.cleanup();
                      observer.next({ blob, duration });
                      observer.complete();
                    } catch {
                      this.cleanup();
                      observer.error('failed_to_stop_recording');
                    }
                  };
                  this.rec!.onerror = () => {
                    this.cleanup();
                    observer.error('failed_to_stop_recording');
                  };

                  const timer = setTimeout(() => {
                    try {
                      this.rec!.stop();
                    } catch {
                    }
                  }, delay);

                  return () => clearTimeout(timer);
                }),
            })),
            catchError((e: any) => {
              this.cleanup();
              if (e?.name === 'NotAllowedError') return throwError(() => 'please_allow_mic');
              return throwError(() => 'failed_to_start_recording');
            }),
          );
        }),
      ),
    );
  }

  cancel(): void {
    try {
      this.rec?.stop();
    } catch {
    }
    this.cleanup();
  }

  private cleanup() {
    this.rec = undefined;
    this.chunks = [];
    this.startedAt = 0;
  }
}
